import { memo, useEffect, useMemo, useState } from "react";

import { Message } from "@chatscope/chat-ui-kit-react";
import { ArrowForwardIos as ArrowForwardIosIcon } from "@mui/icons-material";
import { Collapse, IconButton, Typography } from "@mui/material";
import { Box, useTheme } from "@mui/system";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { PauseCircle, PlayCircle, WaveIcon } from "../../components/Icons/Icons";
import { getMarkdownComponents, getMarkdownStyles } from "./MarkdownStyles.jsx";
import { SoundWaveform } from "./SoundWaveform";
import { VegaChart } from "./VegaChart";
import { ReasoningDisplay } from "./ReasoningDisplay";

// Memoized wrapper to prevent VegaChart from flashing on re-renders
const MemoizedVegaChart = memo(
  ({ spec, title }) => {
    return <VegaChart spec={spec} title={title} />;
  },
  (prevProps, nextProps) => {
    // Deep comparison of spec objects to prevent unnecessary re-renders
    if (prevProps.title !== nextProps.title) return false;

    // If specs are the same reference, no need to re-render
    if (prevProps.spec === nextProps.spec) return true;

    // Deep comparison of spec objects
    try {
      const prevSpecStr = JSON.stringify(prevProps.spec);
      const nextSpecStr = JSON.stringify(nextProps.spec);
      return prevSpecStr === nextSpecStr;
    } catch {
      // If JSON.stringify fails, fall back to reference equality
      return prevProps.spec === nextProps.spec;
    }
  }
);

MemoizedVegaChart.displayName = "MemoizedVegaChart";

// Memoize the renderers object to prevent recreation on every render
const createRenderers = (theme, message) => ({
  ...getMarkdownComponents(theme),
  code: ({ inline, children, ...props }) => {
    if (inline) {
      return (
        <code className="inline-code" {...props}>
          {children}
        </code>
      );
    }

    if (typeof message === "object" && message.$schema && message.$schema.includes("vega-lite")) {
      const title = message.title?.text || message.title || "Chart";
      return <MemoizedVegaChart spec={message} title={title} />;
    }

    const content = typeof children === "string" ? String(children).replace(/\n$/, "") : children;

    try {
      const spec = typeof content === "string" ? JSON.parse(content) : content;
      if (spec.$schema && spec.$schema.includes("vega-lite")) {
        const title = spec.title?.text || spec.title || "Chart";
        return <MemoizedVegaChart spec={spec} title={title} />;
      }
    } catch {
      // TODO error handling
    }

    return (
      <pre className="code-block">
        <code {...props}>{content}</code>
      </pre>
    );
  },
});

export const CustomMessage = ({
  message,
  sender,
  direction,
  audio,
  isAudioMessageLoading,
  registerAudio,
  isBigChatFlag,
  reasoning,
  isStreaming = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState("0:00");
  const [isPlaying, setIsPlaying] = useState(false);
  const [isNotAudioPresent, setIsNotAudioPresent] = useState(false);
  const theme = useTheme();
  function stripHtmlTags(str) {
    if (!str) return "";
    return str.replace(/<[^>]*>?/gm, "");
  }

  useEffect(() => {
    if (!(audio instanceof HTMLAudioElement)) {
      setIsNotAudioPresent(true);
      return;
    } else {
      setIsNotAudioPresent(false);
    }
    const updateProgress = () => {
      const currentTime = audio.currentTime;
      setCurrentTime(currentTime);
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => setIsPlaying(false);

    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("timeupdate", updateProgress);

    return () => {
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("timeupdate", updateProgress);
    };
  }, [audio]);

  useEffect(() => {
    if (audio instanceof HTMLAudioElement) {
      audio.onloadedmetadata = () => {
        const duration = Math.round(audio.duration);
        setAudioDuration(`${Math.floor(duration / 60)}:${String(duration % 60).padStart(2, "0")}`);
      };
    }
  }, [audio]);

  useEffect(() => {
    if (audio instanceof HTMLAudioElement && typeof registerAudio === "function") {
      registerAudio(audio);
    }
  }, [audio, registerAudio]);

  const handleTogglePlay = () => {
    if (!(audio instanceof HTMLAudioElement)) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch((e) => {
        console.error("Audio play failed:", e);
      });
    }

    setIsPlaying(!isPlaying);
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };
  const _currentTime = Math.round(currentTime);
  const currentTimeFormatted = `${Math.floor(_currentTime / 60)}:${String(Math.floor(_currentTime % 60)).padStart(2, "0")}`;

  // Format time as HH:MM AM/PM for today's messages
  // Format as MM/DD HH:MM AM/PM for other days
  const formatSentTime = (sentTime) => {
    const sentDate = new Date(sentTime);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const hours = sentDate.getHours();
    const minutes = String(sentDate.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    const formattedHours = hours % 12 || 12; // Convert 24h to 12h format

    if (sentDate.toDateString() === today.toDateString()) {
      return `${formattedHours}:${minutes} ${ampm}`;
    } else {
      //const month = sentDate.getMonth() + 1; // getMonth() returns 0-11
      //const day = sentDate.getDate();
      //return `${month}/${day} ${formattedHours}:${minutes} ${ampm}`; // Format as MM/DD HH:MM AM/PM for future use do not remove it
      return `${formattedHours}:${minutes} ${ampm}`;
    }
  };

  // Memoize the renderers to prevent recreation on every render
  const renderers = useMemo(() => createRenderers(theme, message), [theme, message]);

  const isVegaSpec = typeof message === "object" && message.$schema && message.$schema.includes("vega-lite");
  const hasMarkdown =
    typeof message === "string" &&
    (message.includes("```") ||
      message.includes("`") ||
      message.includes("**") ||
      message.includes("__") ||
      message.includes("*") ||
      message.includes("_") ||
      message.includes("#") ||
      message.includes("[") ||
      message.includes("](") ||
      message.includes("|") ||
      /^\s*[-*+]\s/.test(message) ||
      /^\s*\d+\.\s/.test(message) ||
      // Detect inline list-like structures with hyphens
      /\s-\s\w+/.test(message) ||
      // Detect multiple hyphens that suggest a list
      (message.match(/-/g) || []).length > 2);

  // Preprocess message to preserve \n characters as line breaks and improve list formatting
  const preprocessMessage = (msg) => {
    if (typeof msg !== "string") return msg;

    let processed = msg;

    // Convert \n to markdown line breaks (two spaces + \n)
    processed = processed.replace(/\n/g, "  \n");

    // If this looks like it has inline list structures, convert them to proper markdown lists
    if (hasMarkdown && /\s-\s\w+/.test(processed)) {
      // Convert " - Item" patterns to proper markdown list items
      processed = processed.replace(/\s-\s/g, "\n- ");
      // Add a line break before the first list item if it's not already there
      if (!processed.startsWith("-")) {
        processed = processed.replace(/([^:]) - /, "$1\n- ");
      }
    }

    return processed;
  };

  const processedMessage = preprocessMessage(message);

  const isAudioMessage = !!audio;

  return (
    <Message
      model={{
        message: isVegaSpec ? JSON.stringify(message, null, 2) : processedMessage,
        sender,
        direction,
        position: "single",
        audio,
      }}
    >
      <Message.CustomContent>
        <Box
          sx={{
            position: "relative",
            p: 1,
            borderRadius: "16px 16px 16px 4px",
            maxWidth: "100%",
            minWidth: 300,
            wordWrap: "break-word",
            overflowWrap: "break-word",
          }}
        >
          {/* Reasoning Display - show for assistant messages */}
          {reasoning && direction === "incoming" && (
            <ReasoningDisplay reasoning={reasoning} isStreaming={isStreaming} />
          )}

          {isAudioMessage && !hasMarkdown && !isVegaSpec && (
            <Collapse in={isExpanded} timeout={300} unmountOnExit>
              <Typography sx={{ whiteSpace: "pre-wrap", mt: 1, lineHeight: 1.4 }}>
                {stripHtmlTags(processedMessage)}
              </Typography>
            </Collapse>
          )}
          {isAudioMessage && (hasMarkdown || isVegaSpec) && (
            <Collapse in={isExpanded} timeout={300} unmountOnExit>
              <Box sx={{ mt: 1, ...getMarkdownStyles(theme) }} className="markdown-content">
                <ReactMarkdown components={renderers} remarkPlugins={[remarkGfm]} skipHtml={true}>
                  {isVegaSpec ? JSON.stringify(message, null, 2) : processedMessage}
                </ReactMarkdown>
              </Box>
            </Collapse>
          )}
          {!isAudioMessage && (isVegaSpec || hasMarkdown) && (
            <Box sx={getMarkdownStyles(theme)} className="markdown-content">
              <ReactMarkdown components={renderers} remarkPlugins={[remarkGfm]} skipHtml={true}>
                {isVegaSpec ? JSON.stringify(message, null, 2) : processedMessage}
              </ReactMarkdown>
            </Box>
          )}

          {!isAudioMessage && !isVegaSpec && !hasMarkdown && (
            <Typography sx={{ whiteSpace: "pre-wrap" }}>{stripHtmlTags(processedMessage)}</Typography>
          )}

          {isAudioMessageLoading && direction === "incoming" && (
            <Box sx={{ display: "flex", alignItems: "center", mt: "15px" }}>
              <IconButton
                sx={{
                  top: "4px",
                  padding: "0px",
                }}
              >
                {/* Note: if you want to two diff wave for the small and big chat then uncomment this */}
                {/* {!isBigChatFlag ? <MinimizeWave /> : <WaveIcon/>} */}
                <WaveIcon />
              </IconButton>
              <Box sx={{ position: "relative", display: "inline-block" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    flexDirection: "column",
                  }}
                >
                  <Typography sx={{ whiteSpace: "pre-wrap" }}> Audio is generating...</Typography>
                </Box>
              </Box>
            </Box>
          )}

          {!isNotAudioPresent && isAudioMessage && !isAudioMessageLoading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: "15px" }}>
              <IconButton
                sx={{
                  bgcolor: "primary.main",
                  "&:hover": { bgcolor: "primary.main" },
                  padding: "0px",
                }}
                onClick={handleTogglePlay}
              >
                {isPlaying ? <PauseCircle /> : <PlayCircle />}
              </IconButton>

              <Box sx={{ position: "relative", display: "inline-block" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    flexDirection: "column",
                    padding: "0px 6px",
                    ...(isBigChatFlag && {
                      width: isBigChatFlag && "180px",
                    }),
                  }}
                >
                  <SoundWaveform audio={audio} isBigChatFlag={true} />
                  <Typography variant="caption" sx={{ display: "block", textAlign: "center", mt: 1, minWidth: 32 }}>
                    {audio?.currentTime > 0 ? `${currentTimeFormatted} / ${audioDuration}` : audioDuration}
                  </Typography>
                </Box>
              </Box>

              <IconButton
                onClick={toggleExpand}
                sx={{
                  transition: "transform 0.3s ease",
                  transform: isExpanded ? "rotate(-90deg)" : "rotate(0deg)",
                }}
              >
                <ArrowForwardIosIcon style={{ color: "text.secondary" }} fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            // Keep it will impliment in future
            // <Button onClick={onGenerateAudio} sx={{ mt: "15px" }}>Generate Audio</Button>
            <></>
          )}

          {/* in future if we want to show message time then uncomment this code */}
          {/* {!isBigChatFlag && (
            <Typography
              variant="caption"
              sx={{
                display: "inherit",
                marginTop: "10px",
                textAlign: "right",
                color: "primary.light",
              }}
            >
              {formatSentTime(sentTime)}
            </Typography>
          )} */}
        </Box>
      </Message.CustomContent>
    </Message>
  );
};
